import {
  Box,
  Stack,
  useTheme,
  TextField,
  CircularProgress,
} from "@mui/material";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { ArrowUp } from "lucide-react";
import { useState, useCallback } from "react";

interface AddCommentProps {
  handleSendComment: (comment: string) => void;
  isPending: boolean;
}

const AddComment = ({ handleSendComment, isPending }: AddCommentProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [comment, setComment] = useState("");

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setComment(e.target.value);
    },
    []
  );

  const onSendComment = useCallback(() => {
    if (!comment.trim() || isPending) return;
    handleSendComment(comment.trim());
    setComment("");
  }, [comment, handleSendComment, isPending]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSendComment();
      }
    },
    [onSendComment]
  );

  const isDisabled = !comment.trim() || isPending;

  return (
    <Stack
      direction="row"
      alignItems="center"
      alignSelf="stretch"
      spacing={theme.nawy.spacing.sm}
      sx={{
        border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
        borderRadius: theme.nawy.radius.xl,
        background: theme.nawy.colors.semantic.layer.primary,
        justifyContent: "space-between",
        transition: "border-color 0.2s ease",
        padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.sm}`,
        "&:focus-within": {
          borderColor: theme.nawy.colors.semantic.border.accent,
        },
        ...(isMobile && {
          width: "100%",
        }),
      }}
    >
      {/* Comment Input */}
      <Box
        sx={{
          flex: 1,
          overflow: "visible",
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          value={comment}
          onChange={handleCommentChange}
          onKeyDown={handleKeyDown}
          placeholder={isMobile ? "Comment" : "Add comment"}
          variant="standard"
          sx={{
            "& .MuiInput-root": {
              "&:before": {
                borderBottom: "none",
              },
              "&:after": {
                borderBottom: "none",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: "none",
              },
            },
            "& .MuiInputBase-input": {
              padding: 0,
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.primary,
              "&::placeholder": {
                color: theme.nawy.colors.semantic.text.tertiary,
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      {/* Circular Send Button */}
      <Box
        onClick={onSendComment}
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: isDisabled
            ? theme.nawy.colors.semantic.layer.secondary
            : theme.nawy.colors.semantic.layer.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isDisabled ? "not-allowed" : "pointer",
          flexShrink: 0,
          transition: "all 0.2s ease",
          opacity: isDisabled ? 0.5 : 1,
          "&:hover": {
            ...(!isDisabled && {
              transform: "scale(1.05)",
              boxShadow: `0 2px 8px ${theme.nawy.colors.semantic.layer.accent}40`,
            }),
          },
          "&:active": {
            ...(!isDisabled && {
              transform: "scale(0.95)",
            }),
          },
        }}
      >
        {isPending ? (
          <CircularProgress
            size={16}
            sx={{
              color: theme.nawy.colors.semantic.text.onColor,
            }}
          />
        ) : (
          <ArrowUp
            size={16}
            color={
              isDisabled
                ? theme.nawy.colors.semantic.text.tertiary
                : theme.nawy.colors.semantic.text.onColor
            }
            strokeWidth={2}
          />
        )}
      </Box>
    </Stack>
  );
};

export default AddComment;
